import BaseScrollTopButton, {
    BaseScrollTopButtonRef,
} from 'base-components/BaseScrollTopButton/BaseScrollTopButton';
import NoData from 'base-components/NoData/NoData';
import Pending from 'base-components/Pending/Pending';
import { useRefWithCallback } from 'hooks/useRefWithCallback';
import useUpdateEffect from 'hooks/useUpdateEffect';
import { isFunction } from 'lodash';
import Filter from 'models/api/filter';
import DataGridProp from 'models/dataGridProp';
import Id from 'models/id';
import { useGridColumnsConfig } from 'providers/GridColumnsConfigProvider';
import { ColumnType, TableData } from 'providers/WorkplaceStateProvider';
import {
    Dispatch,
    PropsWithChildren,
    RefAttributes,
    SetStateAction,
    useMemo,
    useRef,
    useState,
} from 'react';
import ReactDataGrid, {
    CalculatedColumn,
    DataGridHandle,
    DataGridProps,
    FormatterProps,
    HeaderRenderer,
    HeaderRendererProps,
    RowHeightArgs,
    SortColumn,
} from 'react-data-grid';
import { Container } from 'reactstrap';
import 'scss/Main.scss';
import { columnsToColumnsConfig } from 'utils/formatters';
import './BaseDataGrid.scss';
import BaseDataGridHeaderRenderer from './BaseDataGridHeaderRenderer';
import BaseDataGridTooltipedFormatter from './BaseDataGridTooltipedFormatter';

type DraggableHeaderRendererProps<T extends Id, TSummaryRow> = HeaderRendererProps<
    T,
    TSummaryRow
> & {
    onSortColumnsChange: Dispatch<SetStateAction<SortColumn[]>>;
    onColumnsReorder: (sourceKey: string, targetKey: string) => void;
    columnsForSorting?: {
        key: string;
        name: string;
    }[];
    sortColumns: SortColumn[];
    tooltipText?: string;
    tooltipAlwaysShow?: boolean;
};

export function DraggableHeaderRenderer<T extends Id, TSummaryRow = unknown>({
    onColumnsReorder,
    onSortColumnsChange,
    column,
    columnsForSorting,
    sortColumns,
    tooltipText,
    tooltipAlwaysShow,
    ...props
}: DraggableHeaderRendererProps<T, TSummaryRow>) {


    return (
        <div
            style={{
                opacity: 1,
                backgroundColor: undefined,
            }}
        >
            <BaseDataGridHeaderRenderer
                onSortColumnsChange={onSortColumnsChange}
                headerProps={{ ...props, column: column }}
                columnsForSorting={columnsForSorting}
                sortColumns={sortColumns}
                tooltipText={tooltipText}
                tooltipAlwaysShow={tooltipAlwaysShow}
            />
        </div>
    );
}

export type BaseDataGridProps<
    TableType extends DataGridProp,
    TSummaryRow = unknown,
> = PropsWithChildren<{
    rows: TableType[];
    loading?: boolean;
    onRowClick?: (item: TableType, col: CalculatedColumn<TableType, TSummaryRow>) => void;
    lineHeight?: number | ((args: RowHeightArgs<TableType>) => number);
    reactDataGreedProps?: Partial<
        DataGridProps<TableType, TSummaryRow> & RefAttributes<DataGridHandle>
    >;
    className?: string;
    clickable?: boolean;
    setTableData?: Dispatch<SetStateAction<TableData<TableType>>>;
    blockSize?: string | number;
    minBlockSize?: string | number;
    maxBlockSize?: string | number;
    orderColumn?: string;
    orderDesc?: boolean;
    onOrderChange?: (order: Pick<Filter, 'orderDesc' | 'orderColumn'>) => void;
}>;

const BaseDataGrid = <TableType extends DataGridProp, TSummaryRow = unknown>({
    rows,
    loading = false,
    onRowClick,
    lineHeight = 35,
    blockSize,
    minBlockSize,
    reactDataGreedProps = {},
    className = '',
    clickable = false,
    setTableData,
    children,
    orderColumn,
    orderDesc,
    onOrderChange,
}: BaseDataGridProps<TableType, TSummaryRow>) => {
    const { columns, setColumnsConfig } = useGridColumnsConfig<TableType, TSummaryRow>();
    const [sortColumns, setSortColumns] = useState<SortColumn[]>(
        orderColumn
            ? [
                  {
                      columnKey: orderColumn,
                      direction: !orderDesc ? 'ASC' : 'DESC',
                  },
              ]
            : []
    );

    useUpdateEffect(() => {
        onOrderChange?.({
            orderColumn: sortColumns[0]?.columnKey,
            orderDesc: sortColumns[0]?.direction === 'DESC',
        });
    }, [sortColumns]);

    const topBtnRef = useRef<BaseScrollTopButtonRef>(null);
    const { refCallback: gridRefCallback, ref: gridRef } = useRefWithCallback<DataGridHandle>(
        (node) =>
            node && node.element && node.element.scrollTop > 100
                ? topBtnRef.current?.show()
                : topBtnRef.current?.hide()
    );

    const draggableColumns = useMemo(() => {
        const CustomHeaderRenderer = (
            props: HeaderRendererProps<TableType, TSummaryRow>,
            columnsForSorting?: {
                key: string;
                name: string;
            }[],
            tooltipText?: string,
            tooltipAlwaysShow?: boolean
        ) => (
            <DraggableHeaderRenderer<TableType, TSummaryRow>
                {...props}
                onColumnsReorder={handleColumnsReorder}
                onSortColumnsChange={setSortColumns}
                columnsForSorting={columnsForSorting}
                sortColumns={sortColumns}
                tooltipText={tooltipText}
                tooltipAlwaysShow={tooltipAlwaysShow}
            />
        );

        function handleColumnsReorder(sourceKey: string, targetKey: string) {
            const sourceColumnIndex = columns.findIndex((c) => c.key === sourceKey);
            const targetColumnIndex = columns.findIndex((c) => c.key === targetKey);
            const reorderedColumns = [...columns];

            reorderedColumns.splice(
                targetColumnIndex,
                0,
                reorderedColumns.splice(sourceColumnIndex, 1)[0]
            );

            setColumnsConfig(columnsToColumnsConfig(reorderedColumns));
        }

        return columns
            .filter((x) => x.visible !== false)
            .map((c) => {
                let formater = (props: FormatterProps<TableType, TSummaryRow>) => (
                    <BaseDataGridTooltipedFormatter>
                        {c.formatter instanceof Function
                            ? (c as any).formatter(props)
                            : (props as any).row[props.column.key]}
                    </BaseDataGridTooltipedFormatter>
                );

                if (c.key === 'actions' || c.disableTooltip) {
                    formater = (props: FormatterProps<TableType, TSummaryRow>) =>
                        c.formatter instanceof Function
                            ? (c as any).formatter(props)
                            : (props as any).row[props.column.key];
                }

                if (c.key === 'id' || c.key === 'actions' || c.draggable === false) {
                    const column: ColumnType<TableType, TSummaryRow> = {
                        ...c,
                        sortable: c.key === 'actions' ? false : c.sortable,
                        headerRenderer: (props) =>
                            c.key === 'actions' ? (
                                HeaderRenderer(props)
                            ) : (
                                <BaseDataGridHeaderRenderer
                                    columnsForSorting={c.columnsForSorting}
                                    headerProps={props}
                                    sortColumns={sortColumns}
                                    onSortColumnsChange={setSortColumns}
                                    tooltipText={c.tooltipText}
                                    tooltipAlwaysShow={c.tooltipAlwaysShow}
                                />
                            ),
                        formatter: formater,
                        cellClass: (row) =>
                            `${isFunction(c.cellClass) ? c.cellClass(row) : c.cellClass} ${
                                c.key === 'actions' ? 'overflow-unset' : ''
                            }`,
                    };

                    return column;
                }

                const column: ColumnType<TableType, TSummaryRow> = {
                    ...c,
                    headerRenderer: (props) => (
                        <BaseDataGridTooltipedFormatter showAlways={false}>
                            {CustomHeaderRenderer(
                                props,
                                c.columnsForSorting,
                                c.tooltipText,
                                c.tooltipAlwaysShow
                            )}
                        </BaseDataGridTooltipedFormatter>
                    ),
                    formatter: formater,
                };

                return column;
            });
    }, [columns]);

    return (
        columns && (
            <Container fluid className="base-data-grid" data-testid="base-data-grid">
                <ReactDataGrid<TableType, TSummaryRow>
                    ref={gridRefCallback}
                    className={className + ' base-data-grid__table custom-scrollbar rdg-light'}
                    columns={draggableColumns}
                    onSortColumnsChange={setSortColumns}
                    // todo:
                    rowKeyGetter={(row) => {
                        return row.id as number;

                        // if (row.id !== undefined) {
                        //     return row.id;
                        // } else {
                        //     throw new Error('Id is undefined!');
                        // }
                    }}
                    rowHeight={35}
                    enableVirtualization={false}
                    rows={loading ? [] : rows}
                    style={{
                        blockSize: blockSize,
                        minBlockSize: minBlockSize,
                    }}
                    sortColumns={sortColumns}
                    components={{
                        noRowsFallback: loading ? <Pending /> : <NoData />,
                    }}
                    onRowClick={(el, col) => {
                        //Без таймаута не успевает сброситься выделение
                        clickable &&
                            setTimeout(() => {
                                if (onRowClick && window.getSelection()?.type !== 'Range') {
                                    !(
                                        col as ColumnType<TableType, TSummaryRow> &
                                            CalculatedColumn<TableType>
                                    ).clickDisable && onRowClick(el, col);
                                }
                            }, 1);
                    }}
                    onRowsChange={(data) =>
                        setTableData && setTableData((td) => ({ ...td, data: data }))
                    }
                    onColumnResize={(i, w) => {
                        //TODO ререндерит компонент, поправить
                        setColumnsConfig((prev) => {
                            const config = !prev ? columnsToColumnsConfig(draggableColumns) : prev;

                            if (config) {
                                const col = Object.entries(config).find(
                                    ([_, value]) => value.order === i
                                );

                                if (col) {
                                    return { ...config, [col[0]]: { ...col[1], width: w } };
                                }
                            }

                            return prev;
                        });
                    }}
                    {...{
                        ...reactDataGreedProps,
                        rowClass: (r) => {
                            const result = clickable ? 'clickable' : '';
                            if (reactDataGreedProps.rowClass) {
                                return result + ' ' + reactDataGreedProps.rowClass(r);
                            }

                            return result;
                        },
                    }}
                />
                <BaseScrollTopButton
                    onClick={() => gridRef.current?.scrollToRow(0)}
                    ref={topBtnRef}
                    bottomOffset={children ? '6rem' : '2rem'}
                />
                {children}
            </Container>
        )
    );
};

export default BaseDataGrid;
